import { useQuery } from "@apollo/client";
import { useEffect } from "react";

import { gangImage } from "../unitDetails/BrilliantControlCard";
import {
  RenderConnectivity,
  RenderDeviceReboot,
  getReadableTime,
  isDateMoreThanThirtyMinutesOld,
} from "../../utils/utils";
import {
  EBrilliantControlType,
  IBrilliantControl,
} from "../../interfaces/interfaces";
import { GET_CONTROL_DETAILS } from "../../api/gqlQueries";
import Spinner from "../Spinner";

export interface IControlDetailsProps {
  setDeviceName: (name: string) => void;
  deviceId: string;
  propertyId: string;
}

const ControlDetails: React.FC<IControlDetailsProps> = ({
  setDeviceName,
  deviceId,
  propertyId,
}) => {
  const { data, loading } = useQuery<IBrilliantControl>(GET_CONTROL_DETAILS, {
    variables: { deviceId, propertyId },
  });
  useEffect(() => {
    setDeviceName(data?.brilliantControl.name || "");
  }, [data]);
  if (loading) return <Spinner />;
  if (!data) return <div>No data was returned</div>;
  const isControlOffline = isDateMoreThanThirtyMinutesOld(
    data.brilliantControl.lastKnownCloudSync
  );
  return (
    <>
      <div className="device-details-container">
        <div className="device-details-container-header">
          <div className="device-details-container-left">
            {gangImage(
              data.brilliantControl.numGangs,
              false,
              data.brilliantControl.controlType
            )}
            <div>
              <div className="unit-title hidden-name">
                {data.brilliantControl.name}
              </div>
              <div className="brilliant-card-description-type unit-description-small flex-display uppercase">
                {data.brilliantControl.controlType ===
                EBrilliantControlType.PLUG_IN
                  ? "Plug-In"
                  : `${data.brilliantControl.numGangs}-switch Control`}
              </div>
            </div>
          </div>
          <RenderDeviceReboot
            deviceId={deviceId || ""}
            propertyId={propertyId || ""}
            isDeviceOffline={isControlOffline}
          />
        </div>
      </div>
      <br />
      <div className="brilliant-container-name menu">Device Information</div>
      <div className="device-details-container device-details-container-info">
        <div>
          <div className="sub-heading inactive-font margin-bottom-small">
            Device Name
          </div>
          <div className="tool-tip margin-bottom-small">
            {data.brilliantControl.name}
          </div>
        </div>
        <div>
          <div className="sub-heading inactive-font margin-bottom-small">
            Assigned To
          </div>
          <div className="tool-tip">{data.brilliantControl.roomName}</div>
        </div>
      </div>
      {data.brilliantControl.controlType === EBrilliantControlType.REGULAR && (
        <>
          <br />
          <div className="brilliant-container-name menu">
            Load Configuration
          </div>
        </>
      )}
      {data.brilliantControl.loads?.map((load) => (
        <div className="device-details-container device-details-container-info device-details-container-info-3">
          <div>
            <div className="sub-heading inactive-font margin-bottom-small">
              Load Name
            </div>
            <div className="tool-tip">{load.name}</div>
          </div>
          <div>
            <div className="sub-heading inactive-font margin-bottom-small">
              Load details
            </div>
            <div className="device-details-container-info-load">
              {load.dimmable && (
                <div className="device-details-container-info-load-item tool-tip margin-right-very-small">
                  Dimmable
                </div>
              )}
              {load.loadType && (
                <div className="device-details-container-info-load-item tool-tip margin-right-very-small">
                  {load.loadType}
                </div>
              )}
              {load.lowWattage && (
                <div className="device-details-container-info-load-item tool-tip margin-right-very-small">
                  Low Wattage
                </div>
              )}
              {load.magneticLoad && (
                <div className="device-details-container-info-load-item tool-tip margin-right-very-small">
                  Magnetic Load
                </div>
              )}
              {load.multiWay && (
                <div className="device-details-container-info-load-item tool-tip">
                  Multi-Way
                </div>
              )}
            </div>
          </div>
          <div>
            <div className="sub-heading inactive-font margin-bottom-small">
              Assigned to
            </div>
            <div className="tool-tip">{load.rooms.join(", ")}</div>
          </div>
        </div>
      ))}
      <br />
      <div className="brilliant-container-name menu">Connectivity</div>
      <div className="device-details-container device-details-container-info device-details-container-info-4">
        <div>
          <div className="sub-heading inactive-font margin-bottom-small">
            Network
          </div>
          <div className="tool-tip">{data.brilliantControl.networkName}</div>
        </div>
        <div>
          <div className="sub-heading inactive-font">Signal Strength</div>
          <div className="margin-bottom-small" />
          <RenderConnectivity
            connectionStrength={data.brilliantControl.connectionStrength || 0}
            deviceDetails
            isOfflineDevice={isControlOffline}
          />
        </div>
        <div>
          <div className="sub-heading inactive-font margin-bottom-small">
            Last Software Update
          </div>
          <div className="tool-tip">
            {getReadableTime(data.brilliantControl.lastSoftwareUpdate || "")}
          </div>
        </div>
        <div>
          <div className="sub-heading inactive-font margin-bottom-small">
            Last Cloud Sync
          </div>
          <div className="tool-tip">
            {getReadableTime(data.brilliantControl.lastKnownCloudSync)}
          </div>
        </div>
      </div>
    </>
  );
};

export default ControlDetails;
